import { FC } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { DonutChartProps as Props, DonutChartFeaturedDataType as FeaturedData } from './types'
import { COLORS } from './constants'
import styles from './Charts.module.scss'
import { ContractType } from '../types'

const DonutChart: FC<Props> = ({ contractType, featuredData, responsiveLabels, noLabels }) => {
  // i18n
  const { t } = useTranslation('pricing')

  const size = featuredData === FeaturedData.ALL ? 240 : 170
  const showProducerData = featuredData === FeaturedData.PRODUCER || featuredData === FeaturedData.ALL
  const showBoltData = featuredData === FeaturedData.BOLT || featuredData === FeaturedData.ALL
  const showGovernmentData = featuredData === FeaturedData.GOVERNMENT || featuredData === FeaturedData.ALL
  const showGasData = featuredData === FeaturedData.GAS || featuredData === FeaturedData.ALL
  const producerDasharray = contractType === ContractType.ELECTRICITYGAS ? '125.6, 502.4' : '251.2, 376.8'
  const governmentDasharray = contractType === ContractType.ELECTRICITYGAS ? '251.2, 376.8' : '251.2, 282.6'

  return (
    <div
      className={classNames(styles['donut-chart-container'], {
        [styles['responsive-labels']]: responsiveLabels
      })}
    >
      <div
        className={classNames(styles.chart, {
          [styles['with-gas']]: contractType === ContractType.ELECTRICITYGAS
        })}
      >
        <svg width={size} height={size} viewBox="0 0 254 254">
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd" transform="translate(8.729825, 8.729825)">
            {contractType === ContractType.ELECTRICITYGAS && (
              <path
                stroke={COLORS.purple}
                strokeDasharray="219.8, 408.2"
                strokeWidth={featuredData === FeaturedData.ALL || featuredData === FeaturedData.GAS ? 50 : 35}
                opacity={featuredData === FeaturedData.ALL || featuredData === FeaturedData.GAS ? 1 : 0.85}
                d="M20.6775926,91.3882707 C6.38341143,144.734881 38.0416604,199.568577 91.3882707,213.862758 C144.734881,228.156939 199.568577,196.49869 213.862758,143.15208 C228.156939,89.8054694 196.49869,34.9717737 143.15208,20.6775926 C89.8054694,6.38341143 34.9717737,38.0416604 20.6775926,91.3882707 Z"
              />
            )}
            <path
              stroke={COLORS.green}
              strokeDasharray={producerDasharray}
              strokeWidth={featuredData === FeaturedData.ALL || featuredData === FeaturedData.PRODUCER ? 50 : 35}
              opacity={featuredData === FeaturedData.ALL || featuredData === FeaturedData.PRODUCER ? 1 : 0.85}
              d="M112.03658,17.4072217 C56.8837933,20.2976568 14.5167867,67.3509846 17.4072217,122.503771 C20.2976568,177.656557 67.3509846,220.023564 122.503771,217.133129 C177.656557,214.242694 220.023564,167.189366 217.133129,112.03658 C214.242694,56.8837933 167.189366,14.5167867 112.03658,17.4072217 Z"
            />
            <path
              stroke={COLORS.darkBlue}
              strokeDasharray={governmentDasharray}
              strokeWidth={featuredData === FeaturedData.ALL || featuredData === FeaturedData.GOVERNMENT ? 50 : 35}
              opacity={featuredData === FeaturedData.ALL || featuredData === FeaturedData.GOVERNMENT ? 1 : 0.85}
              d="M153.10697,210.628218 C204.667193,190.836103 230.420333,132.993603 210.628218,81.4333803 C190.836103,29.873157 132.993603,4.12001723 81.4333803,23.9121326 C29.873157,43.7042479 4.12001723,101.546747 23.9121326,153.10697 C43.7042479,204.667193 101.546747,230.420333 153.10697,210.628218 Z"
            />
            <path
              stroke={COLORS.yellow}
              strokeDasharray="31.4, 596.6"
              strokeWidth={featuredData === FeaturedData.ALL || featuredData === FeaturedData.BOLT ? 50 : 35}
              opacity={featuredData === FeaturedData.ALL || featuredData === FeaturedData.BOLT ? 1 : 0.85}
              d="M143.15208,20.6775926 C89.8054694,6.38341143 34.9717737,38.0416604 20.6775926,91.3882707 C6.38341143,144.734881 38.0416604,199.568577 91.3882707,213.862758 C144.734881,228.156939 199.568577,196.49869 213.862758,143.15208 C228.156939,89.8054694 196.49869,34.9717737 143.15208,20.6775926 Z"
            />
          </g>
        </svg>
        {showProducerData && (
          <div
            className={classNames(styles.percentage, styles['percentage-producer'], {
              [styles['is-small']]: featuredData !== FeaturedData.ALL
            })}
          >
            {contractType === ContractType.ELECTRICITYGAS ? '20%' : '40%'}
          </div>
        )}
        {contractType === ContractType.ELECTRICITYGAS && showGasData && (
          <div
            className={classNames(styles.percentage, styles['percentage-gas'], {
              [styles['is-small']]: featuredData !== FeaturedData.ALL
            })}
          >
            35%
          </div>
        )}
        {showBoltData && (
          <div
            className={classNames(styles.percentage, styles['percentage-bolt'], {
              [styles['is-small']]: featuredData !== FeaturedData.ALL
            })}
          >
            5%
          </div>
        )}
        {showGovernmentData && (
          <div
            className={classNames(styles.percentage, styles['percentage-government'], {
              [styles['is-small']]: featuredData !== FeaturedData.ALL
            })}
          >
            {contractType === ContractType.ELECTRICITYGAS ? '40%' : '55%'}
          </div>
        )}
      </div>
      {!noLabels && (
        <div
          className={classNames(styles.labels, {
            [styles['with-gas']]: contractType === ContractType.ELECTRICITYGAS
          })}
        >
          {showProducerData && (
            <div className={styles.label}>
              <div className={styles.dot} style={{ backgroundColor: COLORS.green }} />
              {t('label.producer', 'Jouw energie-opwekker')}
            </div>
          )}
          {showBoltData && (
            <div className={styles.label}>
              <div className={styles.dot} style={{ backgroundColor: COLORS.yellow }} />
              Bolt
            </div>
          )}
          {contractType === ContractType.ELECTRICITYGAS && showGasData && (
            <div className={styles.label}>
              <div className={styles.dot} style={{ backgroundColor: COLORS.purple }} />
              {t('label.gas', 'Gas')}
            </div>
          )}
          {showGovernmentData && (
            <div className={styles.label}>
              <div className={styles.dot} style={{ backgroundColor: COLORS.darkBlue }} />
              {t('label.government-operator', 'De overheid en netbeheerder')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

DonutChart.defaultProps = {
  contractType: ContractType.ELECTRICITY,
  responsiveLabels: false,
  noLabels: false,
  featuredData: FeaturedData.ALL
}

export default DonutChart
